import React, { useState, useEffect } from 'react';
import { ChevronDown, Users, Clock, Monitor, MapPin, Mail, Phone, Building2, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { events, faqs, whyItWorks, aboutContent } from '../mockData';

const LandingPage = () => {
  const [filteredEvents, setFilteredEvents] = useState(events);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filters, setFilters] = useState({
    deliveryMode: 'all',
    duration: 'all',
    audienceSize: 'all'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    city: '',
    phone: '',
    eventsOfInterest: [],
    preferredMonth: '',
    notes: ''
  });

  useEffect(() => {
    let filtered = events;

    if (filters.deliveryMode !== 'all') {
      filtered = filtered.filter(event => event.deliveryMode.toLowerCase() === filters.deliveryMode);
    }

    if (filters.duration !== 'all') {
      filtered = filtered.filter(event => {
        const duration = event.duration.toLowerCase();
        if (filters.duration === 'short') return duration.includes('45 min') || duration.includes('1 hour');
        if (filters.duration === 'medium') return duration.includes('1½') || duration.includes('1¼');
        if (filters.duration === 'long') return duration.includes('2 hours');
        return true;
      });
    }

    if (filters.audienceSize !== 'all') {
      filtered = filtered.filter(event => {
        const size = event.audienceSize.toLowerCase();
        if (filters.audienceSize === 'small') return size.includes('60');
        if (filters.audienceSize === 'large') return size.includes('100') || size.includes('any number') || size.includes('no limit');
        return true;
      });
    }

    setFilteredEvents(filtered);
  }, [filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEventInterestChange = (eventTitle, checked) => {
    setFormData(prev => ({
      ...prev,
      eventsOfInterest: checked 
        ? [...prev.eventsOfInterest, eventTitle]
        : prev.eventsOfInterest.filter(e => e !== eventTitle)
    }));
  };

  // Final, clean handler
const handleSubmit = (e) => {
  e.preventDefault();
  alert("Thank you for your interest! We will contact you within 24 hours to discuss your requirements.");
  e.currentTarget.submit();   // hands the POST to Netlify
};

const scrollToEvents = () => {
    document.getElementById('events').scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg border-b border-[#e1e8ed] backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 md:px-16 lg:px-20 py-5">
          <div className="flex justify-between items-center">
            <div className="flex items-center pl-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_a4227776-dad1-4aef-aac1-8e59c96b66bf/artifacts/xwjewinx_v-shesh%20logo.png" 
                alt="v-shesh logo" 
                className="h-14 w-auto focus:outline-none focus:ring-2 focus:ring-[#057705] focus:ring-offset-2 focus:ring-offset-white rounded p-1"
                tabIndex="0"
              />
            </div>
            <div className="flex items-center space-x-10 md:space-x-12 pr-2">
              <button 
                onClick={scrollToEvents} 
                className="text-black hover:text-[#057705] transition-colors font-medium text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#057705] focus:ring-offset-2 focus:ring-offset-white rounded px-4 py-2"
              >
                Events
              </button>
              <a 
                href="#about" 
                className="text-black hover:text-[#057705] transition-colors font-medium text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#057705] focus:ring-offset-2 focus:ring-offset-white rounded px-4 py-2"
              >
                About
              </a>
              <button 
                onClick={scrollToContact} 
                className="text-black hover:text-[#057705] transition-colors font-medium text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#057705] focus:ring-offset-2 focus:ring-offset-white rounded px-4 py-2"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Modern Design */}
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-[#f8f9fa] to-[#e8f4fd] px-6 sm:px-8 md:px-12 lg:px-16 pt-32 pb-16">
        <div className="w-full max-w-6xl mx-auto text-center">
          <h1 className="heading-2 mb-8 sm:mb-12 max-w-4xl mx-auto text-center text-[#9f4420] font-bold tracking-tight">
            <span className="block">ENGAGING, PRACTICAL</span>
            <span className="block">DISABILITY INCLUSION EVENTS</span>
            <span className="block">FOR WORKPLACES</span>
          </h1>
          
          <div className="bg-gradient-to-r from-[#057705] to-[#068706] backdrop-blur-sm border border-[#057705]/30 rounded-2xl p-8 sm:p-10 mb-10 sm:mb-14 max-w-4xl mx-auto shadow-xl">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center justify-center gap-3">
              <span className="text-3xl">🌟</span>
              Celebrating International Day of Persons with Disabilities
            </h2>
            <p className="text-base sm:text-lg leading-relaxed text-white font-medium">
              October–December marks Disability Employment Awareness Month, culminating on December 3 with the International Day of Persons with Disabilities. Our events are designed to help your organization celebrate with practical, engaging activities that build everyday inclusion.
            </p>
          </div>
          
          <p className="text-lg sm:text-xl md:text-2xl leading-relaxed text-black mb-12 sm:mb-16 max-w-3xl mx-auto font-medium">
            Interactive, outcome-driven sessions that build empathy, break stereotypes, and create inclusive work environments through experiential learning and authentic perspectives.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 justify-center items-center">
            <Button 
              onClick={scrollToContact}
              className="btn-primary px-10 py-5 text-lg w-full sm:w-auto min-w-[220px] focus:outline-none focus:ring-2 focus:ring-[#057705] focus:ring-offset-2 focus:ring-offset-white shadow-xl hover:shadow-2xl transition-all duration-300"
            >
              Let's Connect
            </Button>
            <Button 
              onClick={scrollToEvents}
              variant="outline" 
              className="btn-secondary px-10 py-5 text-lg w-full sm:w-auto min-w-[220px] focus:outline-none focus:ring-2 focus:ring-[#057705] focus:ring-offset-2 focus:ring-offset-white"
            >
              Browse Events
            </Button>
          </div>
        </div>
      </section>

      {/* About Section - Modern Design */}
      <section id="about" className="py-32 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#f8f9fa]/50 to-transparent"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl border border-[#e1e8ed]/50 p-12 md:p-16">
              <h2 className="heading-2 mb-10 text-center text-[#9f4420] font-bold">{aboutContent.title}</h2>
              <p className="body-large text-black leading-relaxed text-center font-medium max-w-4xl mx-auto">
                {aboutContent.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Event Portfolio Section */}
      <section id="events" className="py-24">
        <div className="container mx-auto px-4">
          <h2 className="heading-2 mb-8 text-center text-[#9f4420]">Event Portfolio</h2>
          <div className="text-center mb-12">
            <p className="body-medium text-black max-w-3xl mx-auto">
              Choose from our comprehensive range of events designed to help your organization meaningfully celebrate International Day of Persons with Disabilities and embed disability inclusion as a core value.
            </p>
          </div>
          
          {/* Filters */}
          <div className="mb-12">
            <div className="flex flex-col md:flex-row gap-6 justify-center">
              <div className="flex flex-col gap-2">
                <Label className="caption">Delivery Mode</Label>
                <Select value={filters.deliveryMode} onValueChange={(value) => handleFilterChange('deliveryMode', value)}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Modes</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="on-site">On-site</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label className="caption">Duration</Label>
                <Select value={filters.duration} onValueChange={(value) => handleFilterChange('duration', value)}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Durations</SelectItem>
                    <SelectItem value="short">Short (45min - 1hr)</SelectItem>
                    <SelectItem value="medium">Medium (1-1.5hrs)</SelectItem>
                    <SelectItem value="long">Long (2+ hrs)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label className="caption">Audience Size</Label>
                <Select value={filters.audienceSize} onValueChange={(value) => handleFilterChange('audienceSize', value)}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sizes</SelectItem>
                    <SelectItem value="small">Small (Up to 60)</SelectItem>
                    <SelectItem value="large">Large (100+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Event Cards - Premium Design */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredEvents.map((event) => (
              <Card key={event.id} className="group bg-white border-[#e1e8ed] hover:border-[#057705]/30 transition-all duration-500 overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-1">
                {/* Event Image */}
                <div className="w-full h-56 overflow-hidden relative">
                  <img 
                    src={event.image} 
                    alt={event.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <CardHeader className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <CardTitle className="heading-4 text-[#9f4420] font-bold leading-tight">{event.title}</CardTitle>
                  </div>
                  <CardDescription className="body-small text-black leading-relaxed font-medium">
                    {event.objective}
                  </CardDescription>
                  <div className="flex flex-wrap gap-3 mt-6">
                    <Badge variant="secondary" className="bg-gradient-to-r from-[#057705] to-[#068706] text-white flex items-center gap-1 px-3 py-1 rounded-full font-medium shadow-md">
                      {event.deliveryMode === 'Online' ? <Monitor className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                      {event.deliveryMode}
                    </Badge>
                    <Badge variant="outline" className="border-black text-black flex items-center gap-1 px-3 py-1 rounded-full font-medium hover:bg-[#f8f9fa] transition-colors">
                      <Clock className="w-3 h-3" />
                      {event.duration}
                    </Badge>
                    <Badge variant="outline" className="border-black text-black flex items-center gap-1 px-3 py-1 rounded-full font-medium hover:bg-[#f8f9fa] transition-colors">
                      <Users className="w-3 h-3" />
                      {event.audienceSize}
                    </Badge>
                    {/* Price badge removed */}
                  </div>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <div className="mb-8">
                    <h4 className="body-medium font-bold text-[#9f4420] mb-4">What it involves:</h4>
                    <ul className="space-y-3">
                      {event.whatItInvolves.map((item, index) => (
                        <li key={index} className="body-small text-black flex items-start font-medium">
                          <span className="w-2 h-2 bg-gradient-to-r from-[#057705] to-[#068706] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button 
                    onClick={() => setSelectedEvent(event)}
                    variant="outline"
                    className="w-full btn-secondary group-hover:btn-primary group-hover:bg-[#057705] group-hover:text-white group-hover:border-[#057705] transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white border border-[#cbd6e2] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="heading-3 text-[#9f4420]">{selectedEvent.title}</h3>
                <Button 
                  onClick={() => setSelectedEvent(null)}
                  variant="ghost"
                  className="text-[#5a6c7d] hover:text-[#2c3e50] hover:bg-[#f8f9fa]"
                >
                  ✕
                </Button>
              </div>
              
              {/* Event Image in Modal */}
              <div className="w-full h-64 mb-8 overflow-hidden rounded-lg">
                <img 
                  src={selectedEvent.image} 
                  alt={selectedEvent.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="heading-4 text-[#9f4420] mb-4">Objective</h4>
                  <p className="body-medium text-[#2c3e50] mb-6">{selectedEvent.objective}</p>
                  
                  {selectedEvent.theme && (
                    <>
                      <h4 className="heading-4 text-[#9f4420] mb-4">Theme</h4>
                      <p className="body-medium text-[#2c3e50] mb-6">{selectedEvent.theme}</p>
                    </>
                  )}
                  
                  <h4 className="heading-4 text-[#9f4420] mb-4">What it involves</h4>
                  <ul className="space-y-3 mb-6">
                    {selectedEvent.whatItInvolves.map((item, index) => (
                      <li key={index} className="body-small text-[#2c3e50] flex items-start">
                        <span className="w-2 h-2 bg-[#4a7c59] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="heading-4 text-[#9f4420] mb-4">Session Details</h4>
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      {selectedEvent.deliveryMode === 'Online' ? <Monitor className="w-5 h-5 text-[#4a7c59]" /> : <MapPin className="w-5 h-5 text-[#4a7c59]" />}
                      <span className="body-medium text-[#2c3e50]">{selectedEvent.deliveryMode}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-[#4a7c59]" />
                      <span className="body-medium text-[#2c3e50]">{selectedEvent.duration}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-[#4a7c59]" />
                      <span className="body-medium text-[#2c3e50]">{selectedEvent.audienceSize}</span>
                    </div>
                    {/* Price display removed */}
                    {selectedEvent.language && (
                      <div className="flex items-center gap-3">
                        <span className="w-5 h-5 text-[#4a7c59] font-bold">🗣️</span>
                        <span className="body-medium text-[#2c3e50]">{selectedEvent.language}</span>
                      </div>
                    )}
                    {selectedEvent.cast && (
                      <div className="flex items-center gap-3">
                        <span className="w-5 h-5 text-[#4a7c59] font-bold">🎭</span>
                        <span className="body-medium text-[#2c3e50]">{selectedEvent.cast}</span>
                      </div>
                    )}
                  </div>
                  
                  <h4 className="heading-4 text-[#9f4420] mb-4">Full Description</h4>
                  <p className="body-small text-[#2c3e50] mb-6">{selectedEvent.fullDescription}</p>
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-[#cbd6e2]">
                <Button 
                  onClick={scrollToContact}
                  className="btn-primary px-8 py-3"
                >
                  Request This Event
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Why This Works Section - Premium Design */}
      <section className="py-32 bg-gradient-to-b from-[#f8f9fa] to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23e1e8ed\" fill-opacity=\"0.1\"%3E%3Ccircle cx=\"30\" cy=\"30\" r=\"1\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"}}></div>
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="heading-2 mb-16 text-center text-[#9f4420] font-bold">Why This Works</h2>
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {whyItWorks.map((item, index) => (
              <div key={index} className="group text-center flex flex-col bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-[#e1e8ed]/50 hover:border-[#27ae60]/30 hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-[#4a7c59] to-[#5a8a6b] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white text-2xl font-bold">{index + 1}</span>
                </div>
                <h3 className="heading-4 text-[#9f4420] mb-6 min-h-[3.5rem] flex items-center justify-center leading-tight font-bold">{item.title}</h3>
                <p className="body-medium text-[#4a5568] flex-grow leading-relaxed font-medium">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
<section id="contact" className="py-24 bg-white">
  <div className="container mx-auto px-4">
    <h2 className="heading-2 mb-12 text-center text-[#9f4420]">Let's Connect</h2>
    <div className="max-w-4xl mx-auto">

      {/* REAL <form> so Netlify can process it */}
      <form
        id="contactForm"
        name="contact"
        method="POST"
        data-netlify="true"
        netlify-honeypot="bot-field"
        onSubmit={async (e) => {
          e.preventDefault();
          const data = new FormData(e.target);
          await fetch("/", {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams([...data]).toString(),
          });
          alert("Thank you for your interest! We will contact you within 24 hours to discuss your requirements.");
        }}
        className="space-y-8"
      >
        {/* Netlify-required hidden fields */}
        <input type="hidden" name="form-name" value="contact" />
        <p hidden><label>Don’t fill this out: <input name="bot-field" /></label></p>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="caption text-[#2c3e50] font-medium">Name *</Label>
            <Input
              id="name"
              name="fullName"            // added name
              type="text"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              className="bg-white border-[#cbd6e2] text-[#3d2914] focus:border-[#4a7c59]"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="caption text-[#2c3e50] font-medium">Work Email *</Label>
            <Input
              id="email"
              name="email"               // added name
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              className="bg-white border-[#cbd6e2] text-[#3d2914] focus:border-[#4a7c59]"
              required
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="organisation" className="caption text-[#2c3e50] font-medium">Organisation *</Label>
            <Input
              id="organisation"
              name="organisation"        // added name
              type="text"
              value={formData.organisation}
              onChange={(e) => handleFormChange('organisation', e.target.value)}
              className="bg-white border-[#cbd6e2] text-[#3d2914] focus:border-[#4a7c59]"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city" className="caption text-[#2c3e50] font-medium">City *</Label>
            <Input
              id="city"
              name="city"                // added name
              type="text"
              value={formData.city}
              onChange={(e) => handleFormChange('city', e.target.value)}
              className="bg-white border-[#cbd6e2] text-[#3d2914] focus:border-[#4a7c59]"
              required
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="phone" className="caption text-[#2c3e50] font-medium">Phone</Label>
            <Input
              id="phone"
              name="phone"               // added name
              type="tel"
              value={formData.phone}
              onChange={(e) => handleFormChange('phone', e.target.value)}
              className="bg-white border-[#cbd6e2] text-[#3d2914] focus:border-[#4a7c59]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="preferredMonth" className="caption text-[#2c3e50] font-medium">Preferred Month/Week</Label>
            <Input
              id="preferredMonth"
              name="preferredMonth"      // added name
              type="text"
              placeholder="e.g., December 2024, Second week of January"
              value={formData.preferredMonth}
              onChange={(e) => handleFormChange('preferredMonth', e.target.value)}
              className="bg-white border-[#cbd6e2] text-[#2c3e50] focus:border-[#27ae60] placeholder:text-[#8b95a1]"
            />
          </div>
        </div>

        {/* Events of Interest */}
        <div className="space-y-4">
          <Label className="caption text-[#2c3e50] font-medium">Events of Interest</Label>
          <div className="grid md:grid-cols-2 gap-4">
            {events.map((event) => {
              const checked = formData.eventsOfInterest.includes(event.title);
              return (
                <div key={event.id} className="flex items-start space-x-3">
                  <Checkbox
                    id={`event-${event.id}`}
                    checked={checked}
                    onCheckedChange={(c) => handleEventInterestChange(event.title, c)}
                    className="border-[#cbd6e2] data-[state=checked]:bg-[#4a7c59] data-[state=checked]:text-white"
                  />
                  {/* Native hidden input so Netlify captures selections */}
                  {checked && <input type="hidden" name="events[]" value={event.title} />}
                  <Label htmlFor={`event-${event.id}`} className="body-small text-[#2c3e50] cursor-pointer leading-tight">
                    {event.title}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes" className="caption text-[#2c3e50] font-medium">Additional Notes</Label>
          <Textarea
            id="notes"
            name="notes"                 // added name
            value={formData.notes}
            onChange={(e) => handleFormChange('notes', e.target.value)}
            className="bg-white border-[#cbd6e2] text-[#2c3e50] focus:border-[#27ae60] placeholder:text-[#8b95a1] min-h-[120px]"
            placeholder="Any specific requirements, audience details, or questions you'd like to discuss..."
          />
        </div>

        <div className="flex justify-center">
          <Button type="submit" className="btn-primary px-12 py-4 text-lg">
            Submit
          </Button>
        </div>
      </form>

    </div>
  </div>
</section>



      {/* Footer */}
      <footer className="py-12 bg-[#f8f9fa] border-t border-[#cbd6e2]">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="heading-4 text-[#9f4420] mb-4">v-shesh</div>
            <div className="flex flex-col items-center gap-3 mb-4">
              <div className="flex justify-center items-center gap-2">
                <Mail className="w-4 h-4 text-[#5a6c7d]" />
                <span className="body-small text-[#2c3e50]">raja@v-shesh.com | rashi@v-shesh.com | shanthi.k@v-shesh.com</span>
              </div>
              <div className="flex justify-center items-center gap-2">
                <Phone className="w-4 h-4 text-[#5a6c7d]" />
                <span className="body-small text-[#2c3e50]">Rajasekharan: +91 9600030311 | Rashi: +91 9643718789</span>
              </div>
            </div>
            <p className="body-small text-[#2c3e50]">
              Creating inclusive workplaces through engaging disability awareness experiences for International Day of Persons with Disabilities
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;